// FUNCTIONALITY-115C-R2 — pure scroll helpers (testable without JSX).

export function resolveStoreScrollRoot(
  doc: Pick<Document, "querySelector"> = typeof document !== "undefined" ? document : ({ querySelector: () => null } as unknown as Document),
): HTMLElement | null {
  try {
    return doc.querySelector<HTMLElement>("[data-store-scroll-root='true'], .sv2-root");
  } catch {
    return null;
  }
}

export function prefersReducedMotion(
  win: Pick<Window, "matchMedia"> = typeof window !== "undefined" ? window : ({ matchMedia: () => ({ matches: false }) } as unknown as Window),
): boolean {
  try {
    return win.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch {
    return false;
  }
}

export function scrollBehaviorFor(win?: Pick<Window, "matchMedia">): ScrollBehavior {
  return prefersReducedMotion(win) ? "auto" : "smooth";
}

export function absoluteBottomTop(scrollHeight: number, clientHeight: number): number {
  return Math.max(0, scrollHeight - clientHeight);
}

export function scrollStoreToAbsoluteTop(
  root: { scrollTo: (opts: ScrollToOptions) => void } | null = resolveStoreScrollRoot(),
  behavior: ScrollBehavior = scrollBehaviorFor(),
  win: Pick<Window, "scrollTo"> = typeof window !== "undefined" ? window : ({ scrollTo: () => undefined } as unknown as Window),
): void {
  if (root) {
    root.scrollTo({ top: 0, left: 0, behavior });
    return;
  }
  win.scrollTo({ top: 0, left: 0, behavior });
}

export function scrollStoreToAbsoluteBottom(
  root: { scrollHeight: number; clientHeight: number; scrollTo: (opts: ScrollToOptions) => void } | null = resolveStoreScrollRoot(),
  behavior: ScrollBehavior = scrollBehaviorFor(),
  schedule: {
    raf: (cb: FrameRequestCallback) => number;
    timeout: (cb: () => void, ms: number) => unknown;
  } = {
    raf: typeof requestAnimationFrame === "function" ? requestAnimationFrame : (cb) => {
      cb(0);
      return 0;
    },
    timeout: typeof setTimeout === "function" ? setTimeout : ((cb: () => void) => {
      cb();
      return 0;
    }) as typeof setTimeout,
  },
  win: Pick<Window, "scrollTo" | "innerHeight"> & { document?: { documentElement: { scrollHeight: number } } } =
    typeof window !== "undefined"
      ? window
      : ({ scrollTo: () => undefined, innerHeight: 0, document: { documentElement: { scrollHeight: 0 } } } as unknown as Window),
): void {
  const apply = () => {
    if (root) {
      root.scrollTo({ top: absoluteBottomTop(root.scrollHeight, root.clientHeight), left: 0, behavior });
      return;
    }
    const docEl = win.document?.documentElement;
    const top = absoluteBottomTop(docEl?.scrollHeight ?? 0, win.innerHeight ?? 0);
    win.scrollTo({ top, left: 0, behavior });
  };

  apply();
  schedule.raf(() => {
    schedule.raf(() => {
      apply();
      schedule.timeout(apply, 120);
    });
  });
}
